import { FC, useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../hooks';
import { convertCamelCaseToTitleCase, isFailureResponse } from '../../utils';
import { AllStoresDetails } from '../../global-state/types';
import { Animation, Animations, Button, IconWithText } from '../../stories/molecules';
import { Typography, Icons } from '../../stories/atoms';
import { InputSelect } from '../../stories/molecules/inputSelect/InputSelect';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { getBrandId } from '../../constants/brandMapping';
import { requestAccessTranslation, selectRoleTranslation } from '../../translations/selectRole';
import { commonTranslations, profileTranslations } from '../../translations';
import { muiTheme } from '../../theme';
import { ColumnFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { useThemeContext } from '../../global-state/themeContext';
import { StyledButtonWrapper } from './RequestNewRole.styles';

type RequestStatus = 'loading' | 'idle' | 'error' | 'access-request-error' | 'success';

type RequestNewRoleProps = {
  onRequestSuccess: () => void;
  showBackButton?: boolean;
};

export const RequestNewRole: FC<RequestNewRoleProps> = observer(({ onRequestSuccess, showBackButton = false }) => {
  const { t } = useTranslation();
  const { fetchAllStoresDetails } = useStores();
  const { userAccessRequest } = useUserPermissions();
  const { mode } = useThemeContext();

  const {
    header,
    subHeader,
    fetchingRoles,
    errorFetchingRoles,
    errorRequestAccess,
    inputForm: { label, placeholder, button },
    successRequestAccess,
  } = requestAccessTranslation;

  const { tryAgain, errorText1 } = commonTranslations;
  const { role } = profileTranslations;

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const [status, setStatus] = useState<RequestStatus>('idle');
  const [allStoresDetails, setAllStoresDetails] = useState<AllStoresDetails>({} as AllStoresDetails);
  const [selectedRole, setSelectedRole] = useState<AllStoresDetails['jobRoles'][0] | null>(null);
  const [selectedOrganisation, setSelectedOrganisation] = useState<AllStoresDetails['organisations'][0] | null>(
    null,
  );
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<AllStoresDetails['businessUnits'][0] | null>(
    null,
  );
  const [selectedCountry, setSelectedCountry] = useState<AllStoresDetails['countryCodes'][0] | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string>('');

  // Fetch all store details
  useEffect(() => {
    const triggerFetchAllStoresDetails = async () => {
      setStatus('loading');
      const response = await fetchAllStoresDetails();
      if (isFailureResponse(response)) {
        setStatus('error');
        return;
      }
      setAllStoresDetails(response.data);
      setStatus('idle');
    };
    triggerFetchAllStoresDetails();
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      handleReset();
    }
  }, [status]);

  const handleReset = () => {
    setSelectedRole(null);
    setSelectedOrganisation(null);
    setSelectedBusinessUnit(null);
    setSelectedCountry(null);
    setSelectedBrand('');
  };

  // Call the API to request access
  const triggerRequestAccess = async () => {
    if (!selectedRole) return;

    const requestBody = {
      jobRoleCode: selectedRole.jobRoleCode,
      countryCode: selectedCountry ?? 'NOT SET',
      organizationId: selectedOrganisation?.organisationId ?? 'NOT SET',
      organizationName: selectedOrganisation?.organisationName ?? 'NOT SET',
      businessUnitId: selectedBusinessUnit?.businessUnitId ?? 'NOT SET',
      businessUnitName: selectedBusinessUnit?.businessUnitName ?? 'NOT SET',
      brandId: getBrandId(selectedBrand),
    };

    const response = await userAccessRequest(requestBody);
    if (isFailureResponse(response)) {
      setStatus('access-request-error');
      return;
    }

    setStatus('success');
    // After showing success for 2 seconds, move to status view
    setTimeout(() => {
      onRequestSuccess();
    }, 2000);
  };

  const handleSubmit = async () => {
    await triggerRequestAccess();
  };

  const renderLoading = () => (
    <Animation title={t(fetchingRoles.key, fetchingRoles.defaultValue)} animation={Animations.LOADING} />
  );

  const renderError = () => {
    const isAccessError = status === 'access-request-error';
    return (
      <Animation
        title={t(errorText1.key, errorText1.defaultValue)}
        subtitle={t(
          isAccessError ? errorRequestAccess.key : errorFetchingRoles.key,
          isAccessError ? errorRequestAccess.defaultValue : errorFetchingRoles.defaultValue,
        )}
        animation={isAccessError ? Animations.CONFUSED : Animations.SAD}
        button={
          isAccessError
            ? { label: t(tryAgain.key, tryAgain.defaultValue), onClick: () => setStatus('idle') }
            : undefined
        }
      />
    );
  };

  const renderSuccess = () => (
    <Animation
      title={t(successRequestAccess.title.key, successRequestAccess.title.defaultValue)}
      subtitle={t(successRequestAccess.subTitle.key, successRequestAccess.subTitle.defaultValue)}
      animation={Animations.HAPPY}
    />
  );

  if (status === 'loading') return renderLoading();
  if (status === 'error' || status === 'access-request-error') return renderError();
  if (status === 'success') return renderSuccess();

  const isHMBrand = getBrandId(selectedBrand) === 0;

  const roleMenuItems =
    allStoresDetails.jobRoles
      ?.filter((jobRole) => !(isHMBrand && jobRole.scope === 'Region'))
      .map((jobRole) => convertCamelCaseToTitleCase(jobRole.jobDescription)) ?? [];

  const businessUnitNameCounts = (() => {
    if (!selectedRole || selectedRole.scope !== 'Region') return {};
    const businessUnits = allStoresDetails.businessUnits ?? [];
    return businessUnits.reduce((acc, bu) => {
      acc[bu.businessUnitName] = (acc[bu.businessUnitName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  })();

  const formatBusinessUnitName = (bu: AllStoresDetails['businessUnits'][number]) =>
    businessUnitNameCounts[bu.businessUnitName] > 1
      ? `${bu.businessUnitName} (${bu.businessUnitId})`
      : bu.businessUnitName;

  const organisationMenuItems = (() => {
    if (!selectedRole) return [];

    const scope = selectedRole.scope;
    const selectedBrandId = getBrandId(selectedBrand);

    if (scope === 'Region') {
      const businessUnits = (allStoresDetails.businessUnits ?? []).filter((bu) => bu.brandId === selectedBrandId);

      return businessUnits.map(formatBusinessUnitName);
    } else if (scope === 'Area') {
      const organisations = (allStoresDetails.organisations ?? []).filter(
        (org) => org.brandId === selectedBrandId,
      );

      return organisations.map((org) => org.organisationName);
    }

    return [];
  })();

  const scope = selectedRole?.scope;
  const showOrganisationOrBusinessField = scope === 'Area' || scope === 'Region';
  const showCountryField = scope === 'Country' || scope === 'Sales Market';
  const showBusinessField = scope === 'Region';

  const isFormatValid = () => {
    return (
      selectedRole &&
      selectedBrand &&
      (showOrganisationOrBusinessField ? selectedOrganisation || selectedBusinessUnit : true) &&
      (showCountryField ? selectedCountry : true)
    );
  };

  return (
    <ColumnFlexContainer
      data-testid="request-new-role"
      height={isMobile ? '100%' : 'auto'}
      backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
      padding={isMobile ? [7] : [7, 10]}
      gap={[7]}
      alignItems="start"
    >
      {showBackButton && (
        <IconWithText
          label={t(commonTranslations.back.key, commonTranslations.back.defaultValue)}
          iconOptions={{ icon: Icons.ARROW, rotate: 'up' }}
          textOptions={{ type: 'body3' }}
          onClick={onRequestSuccess}
        />
      )}
      <ColumnFlexContainer alignItems="start">
        <IconWithText
          label={t(header.key, header.defaultValue)}
          iconOptions={{ icon: Icons.KEY, size: 'small' }}
          textOptions={{ type: 'h4' }}
          padding={[0, 0, 2]}
        />
        <Typography type="body3">{t(subHeader.key, subHeader.defaultValue)}</Typography>
      </ColumnFlexContainer>

      <InputSelect
        label={t(label.brand.key, label.brand.defaultValue)}
        placeholder={t(placeholder.selectBrand.key, placeholder.selectBrand.defaultValue)}
        menuItems={allStoresDetails.brands ?? []}
        value={selectedBrand}
        onChange={(val) => {
          const newBrand = val as string;
          setSelectedBrand(newBrand);
          if (getBrandId(newBrand) === 0 && selectedRole?.scope === 'Region') {
            setSelectedRole(null);
            setSelectedOrganisation(null);
            setSelectedBusinessUnit(null);
            setSelectedCountry(null);
          }
        }}
        isFullWidth
        isRequired
      />

      <InputSelect
        label={t(role.key, role.defaultValue)}
        placeholder={t(selectRoleTranslation.label.key, selectRoleTranslation.label.defaultValue)}
        menuItems={roleMenuItems}
        isRequired
        value={selectedRole ? convertCamelCaseToTitleCase(selectedRole.jobDescription) : ''}
        onChange={(value) => {
          const matched = allStoresDetails.jobRoles?.find(
            (jobRole) => convertCamelCaseToTitleCase(jobRole.jobDescription) === value,
          );
          setSelectedRole(matched ?? null);
          setSelectedOrganisation(null);
          setSelectedBusinessUnit(null);
          setSelectedCountry(null);
        }}
        isFullWidth
      />

      {showOrganisationOrBusinessField && (
        <InputSelect
          label={
            showBusinessField
              ? t(label.business.key, label.business.defaultValue)
              : t(label.organization.key, label.organization.defaultValue)
          }
          placeholder={
            showBusinessField
              ? t(placeholder.selectBusiness.key, placeholder.selectBusiness.defaultValue)
              : t(placeholder.selectOrganization.key, placeholder.selectOrganization.defaultValue)
          }
          menuItems={organisationMenuItems}
          value={
            showBusinessField
              ? selectedBusinessUnit
                ? formatBusinessUnitName(selectedBusinessUnit)
                : ''
              : selectedOrganisation?.organisationName ?? ''
          }
          onChange={(value) => {
            if (showBusinessField) {
              const matchedBusinessUnit = allStoresDetails.businessUnits?.find((bu) => {
                return formatBusinessUnitName(bu) === value;
              });
              setSelectedBusinessUnit(matchedBusinessUnit ?? null);
              setSelectedOrganisation(null);
            } else {
              const matchedOrg = allStoresDetails.organisations?.find((org) => org.organisationName === value);
              setSelectedOrganisation(matchedOrg ?? null);
              setSelectedBusinessUnit(null);
            }
          }}
          isRequired
          isFullWidth
        />
      )}

      {showCountryField && (
        <InputSelect
          label={t(label.country.key, label.country.defaultValue)}
          placeholder={t(placeholder.selectCountry.key, placeholder.selectCountry.defaultValue)}
          menuItems={allStoresDetails.countryCodes ?? []}
          value={selectedCountry}
          onChange={(value) => setSelectedCountry(value as string)}
          isFullWidth
          isRequired
        />
      )}

      <StyledButtonWrapper data-testid="button-wrapper">
        <Button
          label={t(button.reset.key, button.reset.defaultValue)}
          variant="secondary"
          onClick={handleReset}
          fullWidth
          disabled={!selectedRole}
        />
        <Button
          label={t(button.requestAccess.key, button.requestAccess.defaultValue)}
          onClick={handleSubmit}
          fullWidth
          disabled={!isFormatValid()}
        />
      </StyledButtonWrapper>
    </ColumnFlexContainer>
  );
});
